/**
 * BehaviorTreeComponent - runs behavior trees on actors. Manages blackboard,
 * root node execution, and tree lifecycle.
 */

import { SceneComponent } from '../../components/SceneComponent.js';
import { AssetPath } from '../../storage/storageProvider.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { BehaviorStatus } from './BehaviorNode.js';
import { BehaviorAction } from './behaviors/BehaviorAction.js';
import { Blackboard } from './blackboard/Blackboard.js';
import { BehaviorTreeLoader } from './loader/BehaviorTreeLoader.js';
import { SelectorNode } from './nodes/composite/SelectorNode.js';
import { SequenceNode } from './nodes/composite/SequenceNode.js';

import type { BehaviorNode } from './BehaviorNode.js';
import type { SceneComponentOptions } from '../../components/SceneComponent.js';
import type { JsonPath } from '../../utils/serialization/decorator.js';

export interface BehaviorTreeComponentOptions extends SceneComponentOptions {
  /** The root node of the behavior tree */
  rootNode?: BehaviorNode;
  /** Path to a JSON file containing the behavior tree definition */
  behaviorTreePath?: string;
  /** How often to update the behavior tree (in seconds) */
  updateInterval?: number;
  /** Whether to automatically start the behavior tree on begin play */
  autoStart?: boolean;
}

/**
 * Component that manages and executes a behavior tree for an NPC.
 * This is the main component that should be attached to NPCs that need AI behavior.
 */
@EngineClass('BehaviorTreeComponent')
export class BehaviorTreeComponent extends SceneComponent {


  @property()
  public updateInterval: number = 0.1; // Update 10 times per second

  @property()
  public autoStart: boolean = true;

  @property()
  public behaviorTreePath: JsonPath | null = null;

  // properly serialize rootNode, or simply setRootNode() before beginPlay()
  public rootNode: BehaviorNode | null = null;

  private blackboard: Blackboard = new Blackboard();
  private isRunning: boolean = false;
  private lastUpdateTime: number = 0;
  private currentStatus: BehaviorStatus = BehaviorStatus.Success;


  constructor() {
    super();
    this.blackboard = new Blackboard();
  }

  public override initialize(options?: BehaviorTreeComponentOptions): void {
    super.initialize(options);
    this.updateInterval = options?.updateInterval ?? this.updateInterval;
    this.autoStart = options?.autoStart ?? this.autoStart;
    this.behaviorTreePath = options?.behaviorTreePath ?? this.behaviorTreePath;
    this.rootNode = options?.rootNode ?? this.rootNode;
  }



  public override postLoad(): void {
    super.postLoad();
  }

  public override beginPlay(): void {
    super.beginPlay();

    // Set up blackboard with owner
    const owner = this.getActor();
    if (owner) {
      this.blackboard = new Blackboard(owner);
    }

    // Initialize root node if we already have one
    if (this.rootNode) {
      this.rootNode.initialize(this.blackboard);
      // Auto-start if enabled
      if (this.autoStart) {
        this.startBehaviorTree();
      }
    }

    // Load behavior tree from file if specified (async)
    if (this.behaviorTreePath && !this.rootNode) {
      this.loadBehaviorTreeFromFile(AssetPath.fromString(this.behaviorTreePath));
    }
  }

  public override endPlay(): void {
    // Stop the behavior tree and clean up resources
    this.stopBehaviorTree();

    // Destroy the root node and all its children
    if (this.rootNode) {
      this.rootNode.destroy();
      this.rootNode = null;
    }

    // Clear the blackboard
    this.blackboard.clear();

    super.endPlay();
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    if (!this.isRunning || !this.rootNode) {
      return;
    }

    // Safety check: don't tick if the actor has been destroyed
    const owner = this.getActor();
    if (!owner || owner.isPlayEnded()) {
      this.stopBehaviorTree();
      return;
    }

    // Update blackboard with current game state
    this.blackboard.updateGameState();

    // Check if it's time to update the behavior tree
    this.lastUpdateTime += deltaTime;
    if (this.lastUpdateTime >= this.updateInterval) {
      this.updateBehaviorTree(deltaTime);
      this.lastUpdateTime = 0;
    }
  }

  public startBehaviorTree(): void {
    if (!this.rootNode) {
      console.warn('BehaviorTreeComponent: Cannot start - no root node set');
      return;
    }


    this.isRunning = true;
    this.lastUpdateTime = 0;
  }

  public stopBehaviorTree(): void {
    this.isRunning = false;
    if (this.rootNode) {
      // Reset the tree to exit any running nodes properly
      this.rootNode.reset();
    }
    this.currentStatus = BehaviorStatus.Success;
    this.lastUpdateTime = 0;
  }

  public setRootNode(node: BehaviorNode): void {
    if (this.rootNode) {
      this.rootNode.destroy();
    }

    this.rootNode = node;

    // Initialize the node if the component has already begun play
    if (this.getActor()?.getWorld()) {
      this.rootNode.initialize(this.blackboard);
    }
  }

  public getBlackboard(): Blackboard {
    return this.blackboard;
  }

  public getCurrentStatus(): BehaviorStatus {
    return this.currentStatus;
  }

  public isRunningBehaviorTree(): boolean {
    return this.isRunning;
  }

  private async updateBehaviorTree(deltaTime: number): Promise<void> {
    if (!this.rootNode) {
      return;
    }

    // Safety check: stop execution if the actor has been destroyed
    const owner = this.getActor();
    if (!owner || owner.isPlayEnded()) {
      this.stopBehaviorTree();
      return;
    }

    try {
      this.currentStatus = await this.rootNode.execute(this.blackboard, deltaTime);

      // If the tree completed (Success or Failure), restart it
      if (this.currentStatus !== BehaviorStatus.Running) {
        this.rootNode.reset();
      }
    } catch (error) {
      console.error('BehaviorTreeComponent: Error executing behavior tree:', error);
      this.currentStatus = BehaviorStatus.Failure;
      this.rootNode.reset();
    }
  }

  private async loadBehaviorTreeFromFile(path: AssetPath): Promise<void> {
    try {
      const { rootNode, blackboardData } = await BehaviorTreeLoader.loadFromFile(path);
      this.setRootNode(rootNode);

      // Set blackboard data if provided
      if (blackboardData && this.blackboard) {
        for (const [key, value] of Object.entries(blackboardData)) {
          this.blackboard.setValue(key, value);
        }
      }

      // Initialize and auto-start if needed (since beginPlay already happened)
      if (this.rootNode && this.blackboard) {
        this.rootNode.initialize(this.blackboard);
        if (this.autoStart) {
          this.startBehaviorTree();
        }
      }
    } catch (error) {
      console.error('BehaviorTreeComponent: Failed to load behavior tree from file:', path, error);
    }
  }

  public getDebugInfo(): any {
    return {
      isRunning: this.isRunning,
      currentStatus: this.currentStatus,
      updateInterval: this.updateInterval,
      blackboard: this.blackboard.getDebugInfo(),
      rootNode: this.rootNode?.getDebugInfo(),
      executionPath: this.getCurrentExecutionPath()
    };
  }

  public getCurrentExecutionPath(): BehaviorNode[] {
    if (!this.rootNode) {
      return [];
    }
    return this.rootNode.getExecutionPath();
  }

  public getCurrentSequence(): SequenceNode | SelectorNode | null {
    const path = this.getCurrentExecutionPath();

    // Find the last composite node in the path (sequence/selector)
    for (let i = path.length - 1; i >= 0; i--) {
      const node = path[i];
      if (node instanceof SequenceNode || node instanceof SelectorNode) {
        return node;
      }
    }

    return null;
  }

  public getCurrentAction(): BehaviorAction | null {
    const path = this.getCurrentExecutionPath();

    // The last node in the path should be the executing action
    if (path.length > 0) {
      const lastNode = path[path.length - 1];
      // Check if it's a leaf node (action or condition)
      if (lastNode instanceof BehaviorAction) {
        return lastNode;
      }
    }

    return null;
  }

  public getCurrentBehaviorDescription(): string {
    if (!this.isRunning) {
      return 'Behavior tree is not running';
    }

    const sequence = this.getCurrentSequence();
    const action = this.getCurrentAction();

    if (sequence && action) {
      return `Executing "${action.name}" in sequence "${sequence.name}"`;
    } else if (action) {
      return `Executing action "${action.name}"`;
    } else if (sequence) {
      return `Running sequence "${sequence.name}"`;
    } else {
      return `Behavior tree is running (status: ${this.currentStatus})`;
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_BehaviorTree';
  }
}